import { useId, useState } from "react"
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Hammer,
  Lightbulb,
  Loader2,
  ShieldCheck,
  ThumbsDown,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const TONE_CLASS = {
  positive: "border-primary/20 bg-primary/10 text-primary",
  warning: "border-chart-4/20 bg-chart-4/10 text-chart-4",
  destructive: "border-destructive/20 bg-destructive/10 text-destructive",
}

const METRIC_TONE_CLASS = {
  positive: "text-primary",
  warning: "text-chart-4",
  destructive: "text-destructive",
}

const METRIC_LABELS = {
  valuePotential: "Value Potential",
  incomePotential: "Income Potential",
  complexity: "Complexity",
  timeToExecute: "Timeline",
}

export function ConceptStrategyCard({
  strategy,
  strategyNumber,
  isSelected,
  isMuted,
  isSelecting,
  isSelectionLocked,
  hasFeedback,
  onSelect,
  onFeedback,
}) {
  const feedbackLabel = hasFeedback
    ? `Feedback logged for ${strategy.name}`
    : `Feedback for ${strategy.name}`
  const selectLabel = isSelected
    ? `Selected strategy: ${strategy.name}`
    : `Select strategy: ${strategy.name}`
  const riskItems = strategy.risks.length > 0
    ? strategy.risks
    : ["No material concept risks flagged by current intelligence."]
  const valueAddLogic = arrayValue(strategy.valueAddLogic)
  const whyThisWorks = arrayValue(strategy.whyThisWorks)
  const hasDistinctWhyThisWorks = !sameList(valueAddLogic, whyThisWorks)
  const [showFullStrategy, setShowFullStrategy] = useState(false)
  const fullStrategyId = useId()

  return (
    <article
      className={cn(
        "relative overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-sm transition-all",
        isSelected && "border-primary/40 bg-primary/5 shadow-md ring-1 ring-primary/20",
        isMuted && "border-border/50 bg-card/70 opacity-70",
      )}
    >
      {isSelected ? (
        <div className="absolute inset-x-0 top-0 h-1 bg-primary" />
      ) : null}

      <div className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Strategy {strategyNumber}
            </span>
            <h3 className="text-lg font-bold tracking-normal text-foreground">
              {strategy.name}
            </h3>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <span
              className={cn(
                "rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider",
                TONE_CLASS[confidenceTone(strategy.confidence, strategy.confidenceScore)],
              )}
            >
              {strategy.confidence}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={feedbackLabel}
              title={feedbackLabel}
              onClick={() => onFeedback?.(strategy.id)}
              className={cn(
                "rounded-lg",
                hasFeedback && "bg-muted/70 text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <ThumbsDown aria-hidden="true" />
            </Button>
          </div>
        </div>

        <p className="mt-4 rounded-lg border border-border bg-muted/30 p-3 text-sm font-medium text-foreground">
          <span className="mr-2 font-bold text-muted-foreground">Purpose:</span>
          {strategy.purpose}
        </p>

        <dl className="mt-5 flex flex-wrap gap-3">
          <MetricItem label={METRIC_LABELS.valuePotential} value={strategy.metrics.valuePotential} />
          <MetricItem label={METRIC_LABELS.incomePotential} value={strategy.metrics.incomePotential} />
          <MetricItem
            label={METRIC_LABELS.complexity}
            value={strategy.metrics.complexity}
            tone={strategy.metrics.complexityTone}
          />
          <MetricItem label={METRIC_LABELS.timeToExecute} value={strategy.metrics.timeToExecute} />
        </dl>

        {!showFullStrategy ? (
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            <StrategySummaryItem
              label="How Value Is Created"
              value={valueAddLogic[0] ?? "Commercial mechanism requires further evidence"}
            />
            <StrategySummaryItem label="Main Risk" value={riskItems[0]} tone="destructive" />
          </div>
        ) : null}

        <Button
          type="button"
          variant="secondary"
          aria-expanded={showFullStrategy}
          aria-controls={fullStrategyId}
          onClick={() => setShowFullStrategy((current) => !current)}
          className="mt-5 w-full justify-between font-bold"
        >
          {showFullStrategy ? "Hide Strategy Detail" : "View Full Strategy"}
          <ChevronDown
            aria-hidden="true"
            className={cn("transition-transform", showFullStrategy && "rotate-180")}
          />
        </Button>

        {showFullStrategy ? (
          <div id={fullStrategyId} className="mt-4 rounded-lg border border-border bg-background p-5">
            <div className="grid gap-x-8 gap-y-6 md:grid-cols-2">
              <DetailList
                icon={Hammer}
                title="Value-Add Logic"
                items={valueAddLogic}
                className={hasDistinctWhyThisWorks ? undefined : "md:col-span-2"}
              />
              {hasDistinctWhyThisWorks ? (
                <DetailList
                  icon={Lightbulb}
                  title="Why This Works"
                  items={whyThisWorks}
                />
              ) : null}

              <section className="border-t border-border pt-4 md:col-span-2">
                <h4 className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  <ShieldCheck className="h-3 w-3" />
                  Development Angle
                </h4>
                <dl className="mt-3 grid gap-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Works Type
                    </dt>
                    <dd className="mt-1 text-sm font-medium text-foreground">
                      {strategy.developmentAngle.works}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Planning Notes
                    </dt>
                    <dd className="mt-1 text-sm font-medium text-foreground">
                      {strategy.developmentAngle.planning}
                    </dd>
                  </div>
                </dl>
              </section>

              <DetailList
                icon={AlertTriangle}
                title="Risks"
                items={riskItems}
                className="border-t border-border pt-4 md:col-span-2"
                itemClassName={strategy.risks.length > 0 ? "text-destructive" : undefined}
              />
            </div>
          </div>
        ) : null}

        <div className="mt-5 flex justify-end">
          <Button
            type="button"
            variant={isSelected ? "default" : "secondary"}
            aria-label={selectLabel}
            disabled={isSelectionLocked && !isSelecting}
            onClick={() => onSelect?.(strategy.id)}
            className="font-bold"
          >
            {isSelecting ? (
              <>
                <Loader2 aria-hidden="true" className="animate-spin" />
                Selecting...
              </>
            ) : isSelected ? (
              <>
                <CheckCircle2 aria-hidden="true" />
                Strategy Selected
              </>
            ) : (
              <>
                Select Strategy
                <ArrowRight aria-hidden="true" />
              </>
            )}
          </Button>
        </div>
      </div>
    </article>
  )
}

function StrategySummaryItem({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4">
      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
      <p
        className={cn(
          "mt-2 text-sm font-semibold leading-relaxed text-foreground",
          tone === "destructive" && "text-destructive",
        )}
      >
        {value}
      </p>
    </div>
  )
}

function confidenceTone(label, score) {
  if (typeof score === "number") {
    if (score >= 75) return "positive"
    if (score >= 55) return "warning"

    return "destructive"
  }

  const normalizedLabel = String(label ?? "").toLowerCase()
  if (normalizedLabel.includes("high")) return "positive"
  if (normalizedLabel.includes("medium")) return "warning"
  if (normalizedLabel.includes("low")) return "destructive"

  return "warning"
}

function MetricItem({ label, value, tone = "positive" }) {
  return (
    <div className="min-w-[7.5rem] rounded-md border border-border bg-background px-3 py-2">
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd
        className={cn(
          "mt-1 text-sm font-semibold text-foreground",
          tone !== "positive" && METRIC_TONE_CLASS[tone],
        )}
      >
        {value}
      </dd>
    </div>
  )
}

function DetailList({ icon: Icon, title, items, className, itemClassName }) {
  return (
    <section className={cn(className)}>
      <h4 className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        <Icon className="h-3 w-3" />
        {title}
      </h4>
      <ul className="mt-3 space-y-2">
        {items.map((item) => (
          <li
            key={item}
            className={cn("flex items-start gap-2 text-sm font-medium leading-relaxed text-foreground", itemClassName)}
          >
            <span aria-hidden="true" className="mt-1 text-primary">-</span>
            {item}
          </li>
        ))}
      </ul>
    </section>
  )
}

function arrayValue(value) {
  return Array.isArray(value) ? value.filter(Boolean) : []
}

function sameList(first, second) {
  if (first.length !== second.length) return false

  return first.every((item, index) => item === second[index])
}
